import { useState } from "react";
import {
  Pressable,
  ScrollView,
  Text,
  View,
  useWindowDimensions,
} from "react-native";
import { Check } from "lucide-react-native";
import {
  Dialog,
  DialogActions,
  DialogHeader,
  DialogPrimaryButton,
  MoonNumericField,
  MoonDateField,
} from "@louise/moon-ui";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { useCreateTestResult } from "@src/packages/test-results/hooks/useTestResults";
import { TestResultDocumentAction } from "./TestResultDocumentAction";
import type { SelectedFile } from "@src/packages/document-vault/DocumentFileDropZone";
import {
  useAmhResults,
  type AmhResult,
  type AmhUnit,
} from "@src/packages/homepage/utils/ovarianReserveInputs";

export type AmhSelection = {
  value: number;
  unit: AmhUnit;
  sourceId?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSelect: (selection: AmhSelection) => void;
  onClear?: () => void;
  onOpenDocuments: (files: SelectedFile[]) => void;
  currentSourceId?: string | null;
  hasActiveAmh?: boolean;
};

function formatDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function ExistingResultRow({
  result,
  selected,
}: {
  result: AmhResult;
  selected: boolean;
}) {
  const content = (
    <>
      <View style={{ flex: 1 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            lineHeight: 20,
            color: EMBER.colors.text,
            fontWeight: "700",
          }}
        >
          {result.value} {result.unit}
        </Text>
        {result.date ? (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 16,
              color: EMBER.colors.textMid,
              marginTop: 2,
            }}
          >
            {formatDate(result.date)}
          </Text>
        ) : null}
      </View>
      {selected ? (
        <Check size={16} color={EMBER.colors.text} strokeWidth={2} />
      ) : null}
    </>
  );

  const style = {
    flexDirection: "row" as const,
    alignItems: "center" as const,
    justifyContent: "space-between" as const,
    gap: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: selected ? "rgba(92, 52, 32, 0.35)" : "rgba(92, 52, 32, 0.12)",
    backgroundColor: selected
      ? "rgba(255,255,255,0.62)"
      : "rgba(255,255,255,0.42)",
    paddingHorizontal: 12,
    paddingVertical: 10,
  };

  return <View style={style}>{content}</View>;
}

function UnitToggle({
  unit,
  onChange,
}: {
  unit: AmhUnit;
  onChange: (next: AmhUnit) => void;
}) {
  const capturePress = usePressCapture("UnitToggle");
  const options: AmhUnit[] = ["ng/mL", "pmol/L"];
  return (
    <View style={{ flexDirection: "row", gap: 6 }}>
      {options.map((option) => {
        const active = option === unit;
        return (
          <Pressable
            key={option}
            onPress={() => {
              // The unit itself is a measurement unit, never a patient value.
              capturePress(pressLabel(option), "amh_unit_toggle");
              onChange(option);
            }}
            accessibilityRole="button"
            accessibilityState={{ selected: active }}
            style={({ pressed }) => ({
              borderRadius: 999,
              borderWidth: 1,
              borderColor: active
                ? "rgba(92, 52, 32, 0.35)"
                : "rgba(92, 52, 32, 0.12)",
              backgroundColor: pressed
                ? "rgba(255,255,255,0.72)"
                : active
                  ? EMBER.colors.text
                  : "rgba(255,255,255,0.5)",
              paddingHorizontal: 12,
              paddingVertical: 6,
            })}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                fontWeight: "700",
                color: active ? "#fff" : EMBER.colors.text,
              }}
            >
              {option}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

export function AmhSourceSheet({
  open,
  onClose,
  onSelect,
  onClear,
  onOpenDocuments,
  currentSourceId,
  hasActiveAmh,
}: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("AmhSourceSheet");
  const ns = "homepage.amh_source_sheet";
  const { height } = useWindowDimensions();
  const existing = useAmhResults();
  const createTestResult = useCreateTestResult();
  const latest = existing[0] ?? null;
  const dialogMaxHeight = Math.min(760, Math.max(420, height - 48));

  const [value, setValue] = useState<number | null>(null);
  const [unit, setUnit] = useState<AmhUnit>("ng/mL");
  const [date, setDate] = useState<string>(
    new Date().toISOString().slice(0, 10),
  );

  const canSave = value !== null && value > 0 && !createTestResult.isPending;

  const highValueWarning: "ng" | "pmol" | null =
    value === null
      ? null
      : unit === "ng/mL" && value > 15
        ? "ng"
        : unit === "pmol/L" && value > 100
          ? "pmol"
          : null;

  const handleAddNew = async () => {
    if (value === null || value <= 0) return;
    try {
      await createTestResult.mutateAsync({
        name: "AMH",
        type: "blood",
        bloodTestCategory: "hormonal",
        patientValue: String(value),
        unit,
        valueType: "numeric",
        date: date || null,
      });
      onSelect({ value, unit });
      setValue(null);
      onClose();
    } catch {
      // surface via mutation error state; keep sheet open
    }
  };

  const handleOpenDocuments = (files: SelectedFile[]) => {
    onClose();
    onOpenDocuments(files);
  };

  return (
    <Dialog
      visible={open}
      onClose={onClose}
      closeButton="minimal"
      containerStyle={{
        width: "90%",
        maxWidth: 420,
        maxHeight: dialogMaxHeight,
      }}
      surfaceStyle={{ maxHeight: dialogMaxHeight }}
      contentStyle={{
        paddingTop: 24,
        paddingHorizontal: 24,
        paddingBottom: 24,
        maxHeight: dialogMaxHeight,
      }}
    >
      <DialogHeader
        title={t(`${ns}.title`)}
        subtitle={t(
          `${ns}.${latest ? "subtitle_with_result" : "subtitle_empty"}`,
        )}
        style={{ marginBottom: 16 }}
      />
      <ScrollView
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        style={{ flexShrink: 1 }}
        contentContainerStyle={{ gap: 16, paddingBottom: 4 }}
      >
        <TestResultDocumentAction onOpenDocuments={handleOpenDocuments} />

        {latest ? (
          <View style={{ gap: 8 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11,
                lineHeight: 14,
                color: EMBER.colors.textLight,
                fontWeight: "700",
                letterSpacing: 0.4,
                textTransform: "uppercase",
              }}
            >
              {t(`${ns}.latest_section`)}
            </Text>
            <ExistingResultRow
              result={latest}
              selected={
                latest.id === currentSourceId || currentSourceId == null
              }
            />
            {existing.length > 1 ? (
              <View style={{ gap: 8, marginTop: 12 }}>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 11,
                    lineHeight: 14,
                    color: EMBER.colors.textLight,
                    fontWeight: "700",
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                  }}
                >
                  {t(`${ns}.previous_section`)}
                </Text>
                {existing.slice(1).map((result) => (
                  <ExistingResultRow
                    key={result.id}
                    result={result}
                    selected={false}
                  />
                ))}
              </View>
            ) : null}
          </View>
        ) : null}

        <View style={{ gap: 8, marginTop: latest ? 20 : 0 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              lineHeight: 14,
              color: EMBER.colors.textLight,
              fontWeight: "700",
              letterSpacing: 0.4,
              textTransform: "uppercase",
            }}
          >
            {t(`${ns}.manual_section`)}
          </Text>
          <View style={{ gap: 12 }}>
            <MoonNumericField
              label={t(`${ns}.value_label`)}
              value={value}
              onChange={setValue}
              placeholder="0.0"
              decimals={2}
              min={0}
            />
            <View style={{ gap: 6 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  lineHeight: 16,
                  color: EMBER.colors.textMid,
                  fontWeight: "600",
                }}
              >
                {t(`${ns}.unit_label`)}
              </Text>
              <UnitToggle unit={unit} onChange={setUnit} />
            </View>
            <MoonDateField
              label={t(`${ns}.date_label`)}
              value={date}
              onChange={setDate}
            />
          </View>
          {highValueWarning ? (
            <View
              style={{
                marginTop: 4,
                paddingHorizontal: 10,
                paddingVertical: 8,
                borderRadius: 10,
                borderWidth: 1,
                borderColor: "rgba(180, 83, 9, 0.25)",
                backgroundColor: "rgba(254, 243, 199, 0.55)",
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  lineHeight: 17,
                  color: "#78350F",
                  fontWeight: "500",
                }}
              >
                {t(
                  highValueWarning === "ng"
                    ? `${ns}.high_value_warning_ng`
                    : `${ns}.high_value_warning_pmol`,
                )}
              </Text>
            </View>
          ) : null}
          {createTestResult.isError ? (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                color: "#B45309",
                marginTop: 4,
              }}
            >
              {t(`${ns}.save_error`)}
            </Text>
          ) : null}
        </View>

        {hasActiveAmh && onClear ? (
          <Pressable
            onPress={() => {
              capturePress(
                pressLabel(t(`${ns}.remove_amh`)),
                `${ns}.remove_amh`,
              );
              onClear();
              onClose();
            }}
            accessibilityRole="button"
            style={({ pressed }) => ({
              alignSelf: "flex-start",
              marginTop: 20,
              paddingHorizontal: 12,
              paddingVertical: 8,
              borderRadius: 999,
              borderWidth: 1,
              borderColor: "rgba(92, 52, 32, 0.14)",
              backgroundColor: pressed
                ? "rgba(255,255,255,0.72)"
                : "rgba(255,255,255,0.42)",
            })}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                lineHeight: 16,
                fontWeight: "700",
                color: EMBER.colors.text,
              }}
            >
              {t(`${ns}.remove_amh`)}
            </Text>
          </Pressable>
        ) : null}

        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            lineHeight: 15,
            color: EMBER.colors.textFaint,
            marginTop: 16,
          }}
        >
          {t(`${ns}.source_line`)}
        </Text>
      </ScrollView>
      <DialogActions style={{ marginTop: 16 }}>
        <DialogPrimaryButton
          label={
            createTestResult.isPending
              ? t(`${ns}.saving`)
              : t(`${ns}.save_and_use`)
          }
          onPress={handleAddNew}
          loading={createTestResult.isPending}
          disabled={!canSave}
        />
      </DialogActions>
    </Dialog>
  );
}
